import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  InputLabel,
  SplitTextField,
  Typography,
} from "@suraasa/placebo-ui"
import { PhoneNumber } from "api/resources/users/types"
import { APIError } from "api/utils"
import clsx from "clsx"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import countryCodes from "utils/countryCodes"
import { mapErrorsV2 } from "utils/helpers"

type PhoneDialogProps = {
  open: boolean
  onOpenChange: () => void
  onSubmit: (phone: PhoneNumber) => void
  error?: APIError | unknown
  isLoading?: boolean
}

type FormType = {
  phoneNumber: PhoneNumber
}

const PhoneDialog = ({
  open,
  onOpenChange,
  onSubmit,
  error,
  isLoading,
}: PhoneDialogProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm<FormType>({
    defaultValues: {
      phoneNumber: {
        code: "",
        number: "",
      },
    },
  })
  useEffect(() => {
    if (error) {
      mapErrorsV2(error, { setter: setError, fields: [["phoneNumber"]] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const options = countryCodes.map(({ dialCode }) => ({
    value: dialCode,
    label: dialCode,
  }))

  const submitForm = handleSubmit(data => {
    if (errors.phoneNumber) return
    onSubmit(data.phoneNumber)
  })

  useEffect(() => {
    if (!open) reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="py-1">
            <Typography variant="title2">Provide your phone number</Typography>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form onSubmit={submitForm}>
            <div className={clsx("flex flex-col gap-2.5")}>
              <div>
                <InputLabel label="Phone Number" required={true} />

                <Controller
                  control={control}
                  name="phoneNumber"
                  rules={{
                    validate: v => {
                      if (!v) return "Required"
                      if (!v.code) return "Code Required"
                      if (!v.number) return "Number Required"
                    },
                  }}
                  render={({ field }) => (
                    <SplitTextField
                      fullWidth
                      error={Boolean(errors.phoneNumber)}
                      helperText={
                        errors.phoneNumber?.number?.message ||
                        errors.phoneNumber?.code?.message ||
                        errors.phoneNumber?.message
                      }
                      selectProps={{
                        mountOnBody: true,
                        placeholder: "+91",
                        options,
                        value: options.find(c => c.value === field.value.code),
                        onChange: val => {
                          if (val) {
                            field.onChange({
                              ...field.value,
                              code: val.value,
                            })
                          }
                        },
                      }}
                      textFieldProps={{
                        type: "number",
                        placeholder: "Enter phone number",
                        value: field.value.number,
                        onChange: e => {
                          field.onChange({
                            ...field.value,
                            number: e.target.value,
                          })
                        },
                      }}
                    />
                  )}
                />
              </div>
              <Button
                loading={isSubmitting || isLoading}
                type="submit"
                onClick={submitForm}
                color="black"
                disabled={!!errors.phoneNumber}
              >
                Reserve My Spot
              </Button>
            </div>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export default PhoneDialog
